import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { formatCurrency, formatDate } from '../utils';
import Modal from '../components/Modal';
import { Btn, Input } from '../components/Input';
import { IconPlus, IconTrash, IconCheck, IconDoc, IconEye, IconEyeOff } from '../components/Icons';
import { useToast } from '../components/Toast';

const TODAY = new Date().toISOString().slice(0, 10);

// ─── helpers ─────────────────────────────────────────────────────────────────

// ─── Modal: oznacz pakiet jako zapłacony ──────────────────────────────────────

export function ZaplacPakietModal({ pakiet, onClose, onSaved }) {
  const { t } = useTranslation();
  const [date, setDate] = useState(TODAY);
  const [saving, setSaving] = useState(false);

  const submit = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.updatePakiet(pakiet.id, { ...pakiet, status: 'zaplacone', data_platnosci: date || null });
      onSaved();
    } finally { setSaving(false); }
  };

  return (
    <Modal title={t('clientDetail.markAsPaid')} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <p style={{ fontSize: '11px', color: 'var(--ink2)' }}>{t('clientDetail.packageLabel')}: <span style={{ color: 'var(--ink)', fontWeight: 600 }}>{pakiet.nazwa}</span></p>
        <Input label={t('spaceDetail.paymentDate')} type="date" value={date} onChange={e => setDate(e.target.value)} />
        <div className="flex justify-end gap-2 pt-2">
          <Btn type="button" variant="ghost" onClick={onClose}>{t('common.cancel')}</Btn>
          <Btn type="submit" variant="success" disabled={saving}>
            <span className="flex items-center gap-1.5"><IconCheck className="w-3.5 h-3.5" /> {t('clientDetail.markAsPaid')}</span>
          </Btn>
        </div>
      </form>
    </Modal>
  );
}

// ─── Modal: Dodaj fakturę (jedno- lub wielospacerową) ────────────────────────

export function WystawFaktureModal({ klient, spacery, faktura = null, onClose, onDone, onDelete, onMarkPaid }) {
  const { t } = useTranslation();
  const toast = useToast();
  const isEdit = !!faktura;
  const today = TODAY;
  const defaultTermin = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  // pozycja = { spacer_id, data_do } — kwota wpisywana raz dla całej faktury.
  // Nowa faktura startuje z czystym formularzem; przycisk „Dodaj wszystkie"
  // pozwala szybko zaciągnąć wszystkie aktywne spacery, gdy to potrzeba.
  const [pozycje, setPozycje] = useState(
    isEdit
      ? (faktura.pozycje || []).map(p => ({
          spacer_id: String(p.spacer_id),
          data_do:   p.data_do || '',
        }))
      : []
  );
  const [kwota, setKwota]             = useState(
    isEdit && faktura?.kwota != null
      ? String(faktura.kwota)
      : (isEdit
          ? String((faktura.pozycje || []).reduce((s, p) => s + (Number(p.kwota) || 0), 0) || '')
          : '')
  );
  const [kwotaTknieta, setKwotaTknieta] = useState(isEdit);
  const [nrFaktury, setNrFaktury]     = useState(faktura?.nr_faktury || '');
  const [dataWystawienia, setDataW]   = useState(faktura?.data_wystawienia?.slice(0,10) || today);
  const [termin, setTermin]           = useState(faktura?.termin_platnosci?.slice(0,10) || defaultTermin);
  const [forma, setForma]             = useState(faktura?.forma_platnosci || 'przelew');
  const [status, setStatus]           = useState(faktura?.status || 'wystawiona');
  const [dataWplaty, setDataWplaty]   = useState(faktura?.data_wplaty?.slice(0,10) || today);
  const [notatki, setNotatki]         = useState(faktura?.notatki || '');
  const [widocznaWPanelu, setWidocznaWPanelu] = useState(
    faktura?.widoczna_w_panelu == null ? 1 : (faktura.widoczna_w_panelu ? 1 : 0)
  );
  const [podglad, setPodglad]         = useState(null);
  const [loading, setLoading]         = useState(false);
  const [err, setErr]                 = useState('');
  // wspólna data dla wszystkich pozycji (opcjonalna)
  const [wspolnaData, setWspolnaData] = useState('');

  // Załącznik PDF — wybrany lokalnie plik (czeka na save), oraz nazwa
  // istniejącego pliku (gdy edytujemy fakturę która już ma PDF).
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfFilename, setPdfFilename] = useState(faktura?.pdf_filename || null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const pickPdfFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) { toast.error('Plik jest większy niż 10 MB'); return; }
      setPdfFile(file);
    };
    input.click();
  };

  const downloadExistingPdf = async () => {
    if (!isEdit || !pdfFilename) return;
    setPdfBusy(true);
    try { await api.downloadFakturaPdf(faktura.id, pdfFilename); }
    catch (e) { toast.error(e.message); }
    finally { setPdfBusy(false); }
  };

  const removeExistingPdf = async () => {
    if (!isEdit || !pdfFilename) return;
    setPdfBusy(true);
    try {
      await api.deleteFakturaPdf(faktura.id);
      setPdfFilename(null);
      toast.success('Usunięto PDF');
    } catch (e) { toast.error(e.message); }
    finally { setPdfBusy(false); }
  };

  useEffect(() => {
    const ready = pozycje.filter(p => p.spacer_id);
    if (ready.length === 0) { setPodglad(null); return; }
    let cancelled = false;
    api.podgladKlientFaktury(klient.id, ready.map(p => ({
      spacer_id: Number(p.spacer_id),
      data_do:   p.data_do || undefined,
    })))
      .then(r => { if (!cancelled) setPodglad(r); })
      .catch(() => { if (!cancelled) setPodglad(null); });
    return () => { cancelled = true; };
  }, [pozycje, klient.id]);

  // Auto-defaultowanie kwoty na sumę sugerowaną — dopóki użytkownik nie tknie pola
  useEffect(() => {
    if (kwotaTknieta) return;
    if (podglad?.suma_sugerowana > 0) {
      setKwota(String(podglad.suma_sugerowana.toFixed(2)));
    }
  }, [podglad, kwotaTknieta]);

  // Nowy wiersz wjeżdża NA GÓRĘ — już zaznaczone spacery zsuwają się w dół.
  const addPozycja  = () => setPozycje(ps => [{ spacer_id: '', data_do: wspolnaData || '' }, ...ps]);
  // Dodaj jednym kliknięciem wszystkie aktywne spacery klienta, które nie są
  // jeszcze na fakturze.
  const addAllSpacery = () => {
    const used = new Set(pozycje.map(p => String(p.spacer_id)).filter(Boolean));
    const fresh = aktywne
      .filter(s => !used.has(String(s.id)))
      .map(s => ({ spacer_id: String(s.id), data_do: wspolnaData || '' }));
    if (fresh.length === 0) return;
    setPozycje(ps => [...fresh, ...ps]);
  };
  const removePozycja = i => setPozycje(ps => ps.filter((_, idx) => idx !== i));
  const updatePozycja = (i, key, val) => setPozycje(ps => ps.map((p, idx) => idx === i ? { ...p, [key]: val } : p));
  const zastosujDateDoWszystkich = () => {
    if (!wspolnaData) return;
    setPozycje(ps => ps.map(p => ({ ...p, data_do: wspolnaData })));
  };

  const submit = async () => {
    if (pozycje.length === 0) { setErr(t('validation.addAtLeastOnePosition')); return; }
    if (!kwota || Number(kwota) <= 0) { setErr(t('validation.invoiceAmountRequired')); return; }
    const payload_pozycje = pozycje.map((p) => {
      const podgl = podglad?.pozycje?.find(pp => String(pp.spacer_id) === p.spacer_id);
      return {
        spacer_id: Number(p.spacer_id),
        data_do:   p.data_do || podgl?.data_do,
        data_od:   podgl?.data_od,
      };
    });
    if (payload_pozycje.some(p => !p.spacer_id || !p.data_do)) {
      setErr(t('validation.positionRequiresSpaceAndDate'));
      return;
    }
    setErr('');
    setLoading(true);
    try {
      const body = {
        nr_faktury: nrFaktury || null,
        data_wystawienia: dataWystawienia,
        termin_platnosci: termin || null,
        forma_platnosci: forma,
        status,
        data_wplaty: status === 'zaplacona' ? (dataWplaty || today) : null,
        notatki: notatki || null,
        kwota: Number(kwota),
        widoczna_w_panelu: widocznaWPanelu ? 1 : 0,
        pozycje: payload_pozycje,
      };
      let fakturaId;
      if (isEdit) {
        await api.updateKlientFaktura(klient.id, faktura.id, body);
        fakturaId = faktura.id;
      } else {
        const created = await api.addKlientFaktura(klient.id, body);
        fakturaId = created?.id;
      }
      // Upload PDF jeśli wybrany — po zapisaniu, kiedy znamy id.
      if (pdfFile && fakturaId) {
        try {
          await api.uploadFakturaPdf(fakturaId, pdfFile);
        } catch (e) {
          toast.error('Faktura zapisana, ale upload PDF nie powiódł się: ' + e.message);
        }
      }
      onDone(); onClose();
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  const aktywne = spacery.filter(s => s.status === 'aktywny');
  // Zbiór spacerów już dodanych na fakturę — używany do ukrywania w dropdownie
  // pozostałych pozycji (każdy spacer może być na fakturze tylko raz).
  const wziete = new Set(pozycje.map(p => String(p.spacer_id)).filter(Boolean));
  const wszystkieZajete = aktywne.length > 0 && aktywne.every(s => wziete.has(String(s.id)));

  return (
    <Modal title={isEdit ? t('clientDetail.editInvoice') : t('clientDetail.addHostingInvoice')} onClose={onClose}>
      <div className="flex flex-col gap-4">

        {/* Pozycje */}
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center gap-2 flex-wrap">
            <span style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>{t('clientDetail.spacesOnInvoice')}</span>
            <div className="flex items-center gap-1.5 flex-wrap">
              <button type="button" onClick={addAllSpacery} disabled={wszystkieZajete}
                className="inline-flex items-center gap-1 rounded-btn"
                style={{
                  background: wszystkieZajete ? 'var(--bg4)' : 'var(--acc)',
                  color: wszystkieZajete ? 'var(--ink3)' : '#fff',
                  border: '1px solid var(--border)',
                  fontSize: '9px', fontWeight: 700, padding: '4px 10px',
                  cursor: wszystkieZajete ? 'not-allowed' : 'pointer',
                  opacity: wszystkieZajete ? 0.6 : 1,
                }}
                title={wszystkieZajete ? t('clientDetail.allSpacesAdded') : t('clientDetail.addAllSpaces')}>
                <IconPlus className="w-3 h-3" /> {t('clientDetail.addAllSpaces')}
              </button>
              <button type="button" onClick={addPozycja} disabled={wszystkieZajete}
                className="inline-flex items-center gap-1 rounded-btn"
                style={{
                  background: 'var(--bg4)', color: 'var(--ink2)',
                  border: '1px solid var(--border)',
                  fontSize: '9px', fontWeight: 700, padding: '4px 10px',
                  cursor: wszystkieZajete ? 'not-allowed' : 'pointer',
                  opacity: wszystkieZajete ? 0.6 : 1,
                }}>
                <IconPlus className="w-3 h-3" /> {t('clientDetail.addSpace')}
              </button>
            </div>
          </div>

          {pozycje.length === 0 && (
            <p style={{ fontSize: '11px', color: 'var(--ink3)', textAlign: 'center', padding: '12px' }}>
              {t('clientDetail.addEachSpace')}
            </p>
          )}

          {/* Wspólna data — dostępna gdy są pozycje */}
          {pozycje.length > 1 && (
            <div style={{ background: 'var(--bg2)', border: '1px dashed var(--border)', borderRadius: '8px', padding: '10px 12px' }}>
              <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                <div className="flex-1">
                  <Input label={t('clientDetail.oneDateForAll')} type="date"
                    value={wspolnaData} onChange={e => setWspolnaData(e.target.value)} />
                </div>
                <button type="button" onClick={zastosujDateDoWszystkich} disabled={!wspolnaData}
                  className="rounded-btn"
                  style={{ background: wspolnaData ? 'var(--acc)' : 'var(--bg4)',
                           color: wspolnaData ? '#fff' : 'var(--ink3)',
                           border: '1px solid var(--border)', fontSize: '10px', fontWeight: 700,
                           padding: '8px 14px', whiteSpace: 'nowrap' }}>
                  {t('clientDetail.applyToAll')}
                </button>
              </div>
            </div>
          )}

          {pozycje.map((p, i) => {
            const podgl = podglad?.pozycje?.find(pp => String(pp.spacer_id) === p.spacer_id);
            const monthsDiff = (a, b) => {
              const da = new Date(a), db = new Date(b);
              return (db.getFullYear() - da.getFullYear()) * 12 + (db.getMonth() - da.getMonth());
            };
            const split = (() => {
              if (!podgl?.data_od || !podgl?.data_do || podgl.miesiace <= 0) return null;
              if (podgl.data_od >= today || podgl.data_do <= today) return null;
              const mZal = Math.max(0, monthsDiff(podgl.data_od, today));
              const mPrz = Math.max(0, monthsDiff(today, podgl.data_do));
              if (mZal === 0 || mPrz === 0) return null;
              return {
                mZal, mPrz,
                kwotaZal: mZal * podgl.stawka_hostingu,
                kwotaPrz: mPrz * podgl.stawka_hostingu,
              };
            })();
            return (
              <div key={i} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '8px', padding: '10px 12px' }}>
                <div className="flex gap-2 items-center">
                  <select value={p.spacer_id} onChange={e => updatePozycja(i, 'spacer_id', e.target.value)}
                    className="ds-input" style={{ flex: 1 }}>
                    <option value="">{t('clientDetail.selectSpace')}</option>
                    {/* Pokazujemy tylko niewzięte spacery — plus aktualnie wybrany w tym wierszu (jeśli jest) */}
                    {aktywne
                      .filter(s => String(s.id) === String(p.spacer_id) || !wziete.has(String(s.id)))
                      .map(s => <option key={s.id} value={s.id}>{s.nazwa}</option>)}
                  </select>
                  <button type="button" onClick={() => removePozycja(i)}
                    className="w-7 h-7 flex items-center justify-center rounded-btn"
                    style={{ color: 'var(--err2)', border: '1px solid var(--border)' }} title={t('clientDetail.removePosition')}>
                    <IconTrash className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="mt-2">
                  <Input label={t('clientDetail.hostingPaidUntil')} type="date"
                    value={p.data_do} onChange={e => updatePozycja(i, 'data_do', e.target.value)} />
                </div>
                {podgl && (
                  <div style={{ fontSize: '10px', color: 'var(--ink3)' }} className="mt-2">
                    {split ? (
                      <div className="flex flex-col gap-0.5">
                        <div style={{ background: 'rgba(176,88,72,0.10)', border: '1px solid rgba(176,88,72,0.25)', borderRadius: '6px', padding: '4px 8px' }}>
                          <span style={{ color: 'var(--err2)', fontWeight: 700 }}>{t('clientDetail.overdueShort')}</span>
                          {' '}{formatDate(podgl.data_od)} → {formatDate(today)} • {split.mZal} mies. = <span style={{ color: 'var(--ink)', fontFamily: 'monospace' }}>{formatCurrency(split.kwotaZal)}</span>
                        </div>
                        <div style={{ background: 'rgba(74,158,186,0.10)', border: '1px solid rgba(74,158,186,0.25)', borderRadius: '6px', padding: '4px 8px' }}>
                          <span style={{ color: 'var(--info, #4a9eba)', fontWeight: 700 }}>{t('clientDetail.future')}</span>
                          {' '}{formatDate(today)} → {formatDate(podgl.data_do)} • {split.mPrz} mies. = <span style={{ color: 'var(--ink)', fontFamily: 'monospace' }}>{formatCurrency(split.kwotaPrz)}</span>
                        </div>
                        <div className="mt-0.5">
                          {t('clientDetail.totalAtRate', { rate: formatCurrency(podgl.stawka_hostingu), amount: formatCurrency(podgl.sugerowana_kwota) })}
                        </div>
                      </div>
                    ) : (
                      <>
                        {t('common.from')} <span style={{ color: 'var(--ink)' }}>{formatDate(podgl.data_od)}</span>
                        {podgl.miesiace > 0 && (
                          <> • {t('clientDetail.monthsAtRate', { months: podgl.miesiace, rate: formatCurrency(podgl.stawka_hostingu), amount: formatCurrency(podgl.sugerowana_kwota) })}</>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Kwota faktury (jedna na cały dokument) */}
        <div className="flex flex-col gap-1">
          <label style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>
            {t('clientDetail.invoiceAmountTitle')} {pozycje.length > 1 ? t('clientDetail.invoiceAmountTotalNote') : ''}
          </label>
          <input type="number" min="0" step="0.01"
            placeholder={podglad?.suma_sugerowana > 0 ? t('clientDetail.suggested', { amount: formatCurrency(podglad.suma_sugerowana) }) : 'np. 540.00'}
            className="ds-input"
            value={kwota}
            onChange={e => { setKwota(e.target.value); setKwotaTknieta(true); }} />
          {podglad?.suma_sugerowana > 0 && Number(kwota) > 0 && Math.abs(Number(kwota) - podglad.suma_sugerowana) > 0.01 && (
            <span style={{ fontSize: '10px', color: 'var(--acc2)' }}>
              {t('clientDetail.amountDiffersSuggested', { amount: formatCurrency(podglad.suma_sugerowana) })}
            </span>
          )}
        </div>

        <Input
          label={t('clientDetail.invoiceNumber')}
          placeholder={(() => {
            const d = new Date();
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            return `np. FV/${y}/${m}/01`;
          })()}
          value={nrFaktury}
          onChange={e => setNrFaktury(e.target.value)}
        />

        <div className="grid sm:grid-cols-2 gap-3">
          <Input label={t('spaceDetail.issueDate')} type="date" value={dataWystawienia} onChange={e => setDataW(e.target.value)} />
          <Input label={t('clientDetail.paymentTerm')} type="date" value={termin} onChange={e => setTermin(e.target.value)} />
        </div>

        <div className="flex flex-col gap-1">
          <label style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>{t('clientDetail.paymentForm')}</label>
          <div className="flex gap-2">
            {[{ id: 'przelew', label: t('clientDetail.transfer') }, { id: 'gotowka', label: t('clientDetail.cash') }].map(f => (
              <button key={f.id} type="button" onClick={() => setForma(f.id)}
                className="flex-1 py-2 rounded-btn transition-colors"
                style={forma === f.id
                  ? { background: 'var(--acc)', color: '#fff', border: '1px solid rgba(184,84,40,0.6)', fontSize: '9px', fontWeight: 700 }
                  : { background: 'var(--bg4)', color: 'var(--ink2)', border: '1px solid var(--border)', fontSize: '9px', fontWeight: 700 }}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>{t('common.status')}</label>
          <div className="flex gap-2">
            {[{ id: 'wystawiona', label: t('spaceDetail.issued') }, { id: 'zaplacona', label: t('spaceDetail.paid') }].map(s => (
              <button key={s.id} type="button" onClick={() => setStatus(s.id)}
                className="flex-1 py-2 rounded-btn transition-colors"
                style={status === s.id
                  ? s.id === 'zaplacona'
                    ? { background: 'rgba(90,158,114,0.18)', color: 'var(--ok2)', border: '1px solid rgba(90,158,114,0.35)', fontSize: '9px', fontWeight: 700 }
                    : { background: 'rgba(184,84,40,0.15)', color: 'var(--acc2)', border: '1px solid rgba(184,84,40,0.25)', fontSize: '9px', fontWeight: 700 }
                  : { background: 'var(--bg4)', color: 'var(--ink2)', border: '1px solid var(--border)', fontSize: '9px', fontWeight: 700 }}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {status === 'zaplacona' && (
          <Input label={t('clientDetail.paymentReceivedDate')} type="date" value={dataWplaty} onChange={e => setDataWplaty(e.target.value)} />
        )}

        <div className="flex flex-col gap-1">
          <label style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>{t('common.notes')}</label>
          <textarea rows={2} className="ds-input resize-none"
            value={notatki} onChange={e => setNotatki(e.target.value)} />
        </div>

        {/* Załącznik PDF — działa zarówno przy tworzeniu (upload po save), jak i edycji (od razu na backend) */}
        <div className="flex flex-col gap-1.5">
          <label style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>
            Załącznik PDF (skan faktury)
          </label>
          {pdfFilename && !pdfFile ? (
            <div className="flex gap-2 flex-wrap items-center">
              <Btn variant="ghost" onClick={downloadExistingPdf} disabled={pdfBusy}>
                <span className="flex items-center gap-1.5"><IconDoc className="w-3 h-3" /> {pdfFilename}</span>
              </Btn>
              <Btn variant="ghost" onClick={pickPdfFile} disabled={pdfBusy}>Zamień</Btn>
              <Btn variant="danger" onClick={removeExistingPdf} disabled={pdfBusy}>Usuń</Btn>
            </div>
          ) : pdfFile ? (
            <div className="flex gap-2 flex-wrap items-center">
              <span style={{ fontSize: '11px', color: 'var(--ink2)', fontWeight: 600, padding: '6px 10px', background: 'var(--bg4)', borderRadius: 'var(--r-btn)', border: '1px solid var(--border)' }}>
                <IconDoc className="w-3 h-3 inline mr-1" /> {pdfFile.name} ({Math.round(pdfFile.size / 1024)} KB)
              </span>
              <Btn variant="ghost" onClick={() => setPdfFile(null)}>Anuluj</Btn>
              <p style={{ fontSize: '10px', color: 'var(--ink3)' }}>{isEdit ? 'Zostanie wgrany po zapisaniu zmian.' : 'Zostanie wgrany po utworzeniu faktury.'}</p>
            </div>
          ) : (
            <div>
              <Btn variant="ghost" onClick={pickPdfFile}>
                <span className="flex items-center gap-1.5"><IconDoc className="w-3 h-3" /> Dodaj PDF</span>
              </Btn>
              <p style={{ fontSize: '10px', color: 'var(--ink3)', marginTop: '4px' }}>
                Klient zobaczy go w swoim panelu i będzie mógł pobrać. Max 10 MB.
              </p>
            </div>
          )}
        </div>

        {/* Widoczność w panelu klienta — togglowalny chip. Domyślnie widoczna. */}
        <div
          onClick={() => setWidocznaWPanelu(v => v ? 0 : 1)}
          style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '10px 12px', borderRadius: '7px',
            background: widocznaWPanelu ? 'rgba(90,158,114,0.07)' : 'var(--bg3)',
            border: `1px solid ${widocznaWPanelu ? 'rgba(90,158,114,0.3)' : 'var(--border)'}`,
            cursor: 'pointer',
          }}
        >
          {widocznaWPanelu
            ? <IconEye className="w-3.5 h-3.5" style={{ color: 'var(--ok2)', flexShrink: 0 }} />
            : <IconEyeOff className="w-3.5 h-3.5" style={{ color: 'var(--ink3)', flexShrink: 0 }} />}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink)', margin: 0 }}>
              {widocznaWPanelu ? 'Widoczna w panelu klienta' : 'Ukryta w panelu klienta'}
            </p>
            <p style={{ fontSize: '10px', color: 'var(--ink3)', margin: '2px 0 0' }}>
              {widocznaWPanelu
                ? 'Klient zobaczy tę fakturę w swoim panelu.'
                : 'Klient nie zobaczy tej faktury — masz ją tylko do swojej ewidencji.'}
            </p>
          </div>
          <div style={{
            width: '30px', height: '17px', borderRadius: '9px',
            background: widocznaWPanelu ? 'var(--ok2)' : 'var(--bg4)',
            border: '1px solid var(--border)', position: 'relative', flexShrink: 0,
          }}>
            <div style={{
              position: 'absolute', top: '2px',
              left: widocznaWPanelu ? '13px' : '2px',
              width: '11px', height: '11px', borderRadius: '50%',
              background: '#fff', transition: 'left 0.15s',
            }} />
          </div>
        </div>

        {err && <p style={{ fontSize: '11px', color: 'var(--err2)' }}>{err}</p>}

        <div className="flex justify-between items-center gap-2 pt-1">
          <div className="flex gap-2">
            {isEdit && onDelete && (
              <Btn variant="danger" onClick={onDelete}>
                <span className="flex items-center gap-1.5"><IconTrash className="w-3.5 h-3.5" /> {t('common.delete')}</span>
              </Btn>
            )}
            {isEdit && onMarkPaid && faktura.status !== 'zaplacona' && (
              <Btn variant="success" onClick={onMarkPaid}>
                <span className="flex items-center gap-1.5"><IconCheck className="w-3.5 h-3.5" /> {t('clientDetail.markPaidShort')}</span>
              </Btn>
            )}
          </div>
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={onClose}>{t('common.cancel')}</Btn>
            <Btn onClick={submit} disabled={loading || pozycje.length === 0 || !kwota || Number(kwota) <= 0}>
              {loading ? t('common.saving') : isEdit ? t('clientDetail.saveChanges') : t('clientDetail.addInvoice')}
            </Btn>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ─── Modal: Oznacz fakturę jako zapłaconą ─────────────────────────────────────

export function OznaczFaktureZaplaconaModal({ klientId, faktura, onClose, onDone }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [date, setDate] = useState(TODAY);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      await api.updateKlientFakturaStatus(klientId, faktura.id, 'zaplacona', date);
      toast.success(t('toast.updated'));
      onDone(); onClose();
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const suma = Number(faktura.suma) || (Number(faktura.kwota) || 0) || (faktura.pozycje || []).reduce((s, p) => s + (Number(p.kwota) || 0), 0);

  return (
    <Modal title={t('clientDetail.markInvoiceAsPaid')} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <div style={{ background: 'var(--bg2)', borderRadius: '8px', padding: '10px 12px' }} className="space-y-0.5">
          {faktura.nr_faktury && <p style={{ fontSize: '11px', color: 'var(--ink2)' }}>{t('clientDetail.invoiceShort')}: <span style={{ color: 'var(--ink)' }}>{faktura.nr_faktury}</span></p>}
          <p style={{ fontSize: '11px', color: 'var(--ink2)' }}>{t('clientDetail.positionsCount')}: <span style={{ color: 'var(--ink)' }}>{(faktura.pozycje || []).length}</span></p>
          <p style={{ fontSize: '11px', color: 'var(--ink2)' }}>{t('common.amount')}: <span style={{ color: 'var(--ink)', fontFamily: 'monospace' }}>{formatCurrency(suma)}</span></p>
        </div>
        <Input label={t('clientDetail.paymentReceivedDate')} type="date" value={date} onChange={e => setDate(e.target.value)} />
        <div className="flex justify-end gap-2">
          <Btn variant="ghost" onClick={onClose}>{t('common.cancel')}</Btn>
          <Btn variant="success" onClick={submit} disabled={loading}>
            <span className="flex items-center gap-1.5"><IconCheck className="w-3.5 h-3.5" /> {t('clientDetail.markPaidShort')}</span>
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
